"use client";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEffect, useRef, useState } from "react";
import JsonInput from "../../form-fields/JsonInput";
import type { TriggerArchetypeProps } from "./types";

/**
 * StripePaymentForm archetype — tabbed Test/Run UX for Stripe-payment-receive.
 *
 * Tab 1 (Quick): single amount input → synthesizes a realistic invoice.paid
 * Stripe event with that amount, matching the schema the B3 stripe-billing
 * workflow already consumes.
 *
 * Tab 2 (Custom payload): pre-filled JSON editor with the same event template.
 * User edits freely and the parsed object is forwarded as the event payload.
 *
 * Both tabs emit { triggeredAt, event } — the shape expected by the
 * stripe-payment-receive trigger's resultSchema (see triggers.json).
 */

// Realistic invoice.paid template — matches the structure documented in
// services/b3os-workflow/docs/STRIPE_PAYMENT_RECEIVE_WORKFLOW.md so the
// existing billing workflow can simulate end-to-end against this payload.
//
// Note: `created` is a placeholder here. Both `buildQuickEvent` and the
// `customJson` state initializer below replace it with a fresh timestamp at
// the moment the user opens the modal — module-level evaluation would leave
// the Custom payload tab showing a stale timestamp on long-lived sessions.
const SAMPLE_STRIPE_INVOICE_PAID = {
  id: "evt_test_1234567890",
  object: "event",
  type: "invoice.paid",
  created: 0,
  data: {
    object: {
      id: "in_test_1234567890",
      object: "invoice",
      amount_paid: 5000,
      amount_due: 5000,
      currency: "usd",
      customer: "cus_test_1234567890",
      customer_email: "test@example.com",
      status: "paid",
      billing_reason: "subscription_cycle",
      parent: {
        type: "subscription_details",
        subscription_details: {
          subscription: "sub_test_1234567890",
          metadata: {
            customerOrgId: "org_test_1234567890",
            tier: "pro",
            interval: "month"
          }
        }
      },
      metadata: {}
    }
  },
  livemode: false
} as const;
function buildQuickEvent(amountDollars: string): Record<string, unknown> {
  const dollars = Number(amountDollars);
  const amountCents = isNaN(dollars) || dollars < 0 ? 0 : Math.round(dollars * 100);
  return {
    ...SAMPLE_STRIPE_INVOICE_PAID,
    created: Math.floor(Date.now() / 1000),
    data: {
      object: {
        ...SAMPLE_STRIPE_INVOICE_PAID.data.object,
        amount_paid: amountCents,
        amount_due: amountCents
      }
    }
  };
}
export function StripePaymentForm({
  onRun
}: TriggerArchetypeProps) {
  const [tab, setTab] = useState<"quick" | "custom">("quick");
  const [amount, setAmount] = useState("50.00");
  // Initialize with a fresh `created` timestamp at modal-open time so the
  // Custom payload tab does not show a stale value baked in at module load.
  const [customJson, setCustomJson] = useState(() => JSON.stringify({
    ...SAMPLE_STRIPE_INVOICE_PAID,
    created: Math.floor(Date.now() / 1000)
  }, null, 2));
  const onRunRef = useRef(onRun);
  useEffect(() => {
    onRunRef.current = onRun;
  });

  // Emit the staged payload to the modal shell whenever inputs change. Both
  // tabs produce { triggeredAt, event } matching the trigger's resultSchema.
  useEffect(() => {
    if (tab === "quick") {
      onRunRef.current({
        triggeredAt: new Date().toISOString(),
        event: buildQuickEvent(amount)
      });
      return;
    }

    // Custom mode: parse the JSON and forward it. If parsing fails (user is
    // mid-typing), leave the previous payload alone — no-op.
    try {
      const parsed = JSON.parse(customJson);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        onRunRef.current({
          triggeredAt: new Date().toISOString(),
          event: parsed as Record<string, unknown>
        });
      }
    } catch {
      // Invalid JSON — keep the previous payload until the user fixes it.
    }
  }, [tab, amount, customJson]);
  return <Tabs value={tab} onValueChange={value => setTab(value as "quick" | "custom")} size="sm" className="space-y-4">
      <TabsList className="w-fit">
        <TabsTrigger value="quick">Quick</TabsTrigger>
        <TabsTrigger value="custom">Custom payload</TabsTrigger>
      </TabsList>

      <TabsContent value="quick" className="mt-0">
        <label className="block">
          <span className="field-label">Amount (USD)</span>
          <div className="border-ds-neutral-300 dark:border-ds-neutral-700 bg-ds-surface-card mt-1 flex items-center gap-2 rounded-lg border px-3 py-2 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20">
            <span className="text-ds-text-muted text-sm">$</span>
            <input type="text" inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} placeholder="50.00" className="text-ds-text-primary placeholder:text-ds-text-disabled w-full bg-transparent text-sm focus:outline-none" />
          </div>
          <p className="caption-xs mt-1.5">
            Generates a realistic Stripe{" "}
            <code className="bg-ds-neutral-100 dark:bg-ds-neutral-800 rounded px-1">invoice.paid</code> event with this
            amount. Available downstream as{" "}
            <code className="bg-ds-neutral-100 dark:bg-ds-neutral-800 rounded px-1">{"{{root.result.event}}"}</code>.
          </p>
        </label>
      </TabsContent>

      <TabsContent value="custom" className="mt-0">
        <div className="space-y-2">
          <JsonInput label="Stripe event JSON" value={customJson} onChange={setCustomJson} placeholder='{"id": "evt_...", "type": "invoice.paid", "data": {"object": {...}}}' />
          <p className="caption-xs">
            Edit the full Stripe event payload directly. Available downstream as{" "}
            <code className="bg-ds-neutral-100 dark:bg-ds-neutral-800 rounded px-1">{"{{root.result.event}}"}</code>.
          </p>
        </div>
      </TabsContent>
    </Tabs>;
}